import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import {
  FaBoxOpen,
  FaBuilding,
  FaComment,
  FaCalendarAlt,
  FaCheck,
  FaCheckCircle,
  FaClock,
  FaClipboardList,
  FaEllipsisV,
  FaEye,
  FaFileAlt,
  FaFileInvoice,
  FaIndustry,
  FaListUl,
  FaMoneyBillWave,
  FaRupeeSign,
  FaInbox,
  FaPaperPlane,
  FaRedo,
  FaSearch,
  FaStar,
  FaTable,
  FaThLarge,
  FaTimesCircle,
  FaTruck,
  FaUser,
} from "react-icons/fa";
import { toast } from "react-toastify";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/styles/logistics_rfq_sent.scss";
import CommonService from "../../services/common.service";
import api from "../../services/api";
import { API_URLS } from "../../constants/api-urls";
import { LOGISTICS_TRANSPORT_MODES } from "../../constants/logisticsProfileFormOptions";
import { buildLogisticsViewRfqPathFromIds } from "../website/logisticsRfqSubmissionStorage";
import { getDashboardRoute } from "../../utils/getDashboardRoute";
import { getCurrentRole } from "../../utils/roleSwitcher";
import {
  DELIVERY_STAGE_CHECKBOXES,
  canOfferManifestAccept,
  getLogisticsRfqSentCardUi,
  isLogisticsStageProgressDone,
} from "../../utils/logisticsRfqSentDeliveryUi";
import { useFactoryDetailsMaster } from "../../hooks/useFactoryDetailsMaster";
import ShippingManifestDetailsModal from "../../components/ShippingManifestDetailsModal";
import LogisticsGrnDetailsModal from "../../components/LogisticsGrnDetailsModal";
import { transformManifestData } from "../../utils/shippingManifestView";
import "../../assets/styles/shipping_manifest_list.scss";
import "../../assets/styles/grn_manifest_list.scss";
import "../../assets/styles/logistics_generate_invoice.scss";

const RFQ_KIND_LABELS = {
  generic: "Generic Logistics",
  "business-relocation": "Business Relocation",
  "exhibition-logistics": "Exhibition Logistics",
};

const PAGE_SIZE = 9;
const LRFSENT_VIEW_STORAGE_KEY = "lrfsent_view_format";

/** Same values as `BusinessRelocationRfqSubmittedView` / business relocation form */
const RELOCATION_SCOPE_OPTIONS = [
  { value: "office", label: "Office Relocation" },
  { value: "employee", label: "Employee Relocation" },
  { value: "both", label: "Both Office & Employee" },
];

function relocationScopeLabel(scope) {
  const s = scope != null ? String(scope).trim().toLowerCase() : "";
  const o = RELOCATION_SCOPE_OPTIONS.find((x) => x.value === s);
  return o ? o.label : scope ? String(scope) : null;
}

/** Same ids as generic logistics RFQ form (`LOGISTICS_TRANSPORT_MODES`) */
function transportModeLabel(modeId) {
  if (modeId == null || String(modeId).trim() === "") return null;
  const id = String(modeId).trim().toLowerCase();
  const m = LOGISTICS_TRANSPORT_MODES.find((x) => x.id === id);
  return m ? m.label : String(modeId);
}

function rfqKindLabel(kind) {
  const k = kind != null ? String(kind).trim() : "";
  if (!k) return "—";
  return RFQ_KIND_LABELS[k] || k;
}

function isDedicatedManagerRfq(row) {
  const s = String(row?.listSource ?? row?.list_source ?? "logistics_generic").trim();
  return s === "exhibition_logistics_rfq" || s === "business_relocation_rfq";
}

function sentRowKey(row) {
  const src = String(row?.listSource ?? row?.list_source ?? "logistics_generic").trim();
  return `${src}:${row?.id}`;
}

function buildSentRfqViewPath(r) {
  if (isDedicatedManagerRfq(r)) {
    const kind = String(r.rfqKind || "").trim();
    if (kind && r.id != null) {
      return `/logistics/rfq/submitted/${encodeURIComponent(kind)}/${encodeURIComponent(String(r.id))}`;
    }
    return null;
  }
  return buildLogisticsViewRfqPathFromIds(
    r.logisticsProviderCompanyId,
    r.id,
    r.rfqReferenceNumber,
    "sent",
  );
}

/** Match `buyer_rfq.js` date formatting */
function formatDate(dateString) {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "N/A";
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "N/A";
  }
}

function getKindTitleClass(kind) {
  const k = kind != null ? String(kind).trim() : "";
  switch (k) {
    case "generic":
      return "lrfsent-card__category lrfsent-card__category--generic";
    case "business-relocation":
      return "lrfsent-card__category lrfsent-card__category--relocation";
    case "exhibition-logistics":
      return "lrfsent-card__category lrfsent-card__category--exhibition";
    default:
      return "lrfsent-card__category";
  }
}

function getKindTableVisual(kind) {
  const k = kind != null ? String(kind).trim() : "";
  switch (k) {
    case "business-relocation":
      return { tone: "relocation", Icon: FaBuilding };
    case "exhibition-logistics":
      return { tone: "exhibition", Icon: FaStar };
    default:
      return { tone: "generic", Icon: FaBoxOpen };
  }
}

function LrfsentHeroIllustration() {
  return (
    <div className="lrfsent-hero__art" aria-hidden>
      <div className="lrfsent-hero__globe" />
      <div className="lrfsent-hero__package lrfsent-hero__package--back" />
      <div className="lrfsent-hero__package lrfsent-hero__package--front" />
      <div className="lrfsent-hero__truck" />
    </div>
  );
}

/** Same idea as `ExhibitionLogisticsRfqSubmittedView` — ISO `YYYY-MM-DD` from form */
/** Match logistics provider “View invoice” modal — ISO timestamps from API. */
function formatInvoiceAcceptedDisplay(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
}

function formatExpoDate(v) {
  if (v == null || String(v).trim() === "") return "—";
  const s = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    try {
      const d = new Date(`${s}T12:00:00`);
      if (!Number.isNaN(d.getTime())) return d.toLocaleDateString();
    } catch {
      /* fall through */
    }
  }
  try {
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) return d.toLocaleDateString();
  } catch {
    /* ignore */
  }
  return s;
}

function getBuyerStatusKey(row) {
  if (isDedicatedManagerRfq(row)) {
    const st = String(row.managerRfqStatus || "pending").trim().toLowerCase();
    if (["completed", "closed", "fulfilled", "done"].includes(st)) return "completed";
    if (["rejected", "cancelled", "canceled"].includes(st)) return "rejected";
    if (["in_progress", "in-progress", "assigned", "processing", "active"].includes(st)) {
      return "accepted";
    }
    return "pending";
  }
  // Once the buyer pays the logistics-provider invoice, the RFQ is fully done.
  if (row?.providerInvoicePaid === true) return "completed";
  if (row.acceptedQuoteId != null && Number(row.acceptedQuoteId) > 0) return "accepted";
  const rejected = Number(row.rejectedQuoteCount || 0);
  const quotes = Number(row.quoteCount || 0);
  if (quotes > 0 && rejected >= quotes) return "rejected";
  if (
    row.isP2pLinked &&
    Number(row.pendingQuoteApprovalCount || 0) > 0 &&
    quotes === 0
  ) {
    return "pending_logistics_approval";
  }
  return "pending";
}

function getBuyerStatusPill(row) {
  const key = getBuyerStatusKey(row);
  if (key === "completed") return { label: "Completed", className: "lrfsent-pill lrfsent-pill--completed" };
  if (key === "accepted") {
    return { label: "In Progress", className: "lrfsent-pill lrfsent-pill--in-progress" };
  }
  if (key === "rejected") return { label: "Rejected", className: "lrfsent-pill lrfsent-pill--rejected" };
  if (key === "pending_logistics_approval") {
    return {
      label: "Pending",
      className: "lrfsent-pill lrfsent-pill--pending",
      title: "Pending Logistics Approval",
    };
  }
  return { label: "Pending", className: "lrfsent-pill lrfsent-pill--pending" };
}

function getP2pInvoicePaymentApprovalLabel(row) {
  if (!row?.isP2pLinked || !row?.providerInvoiceAccepted || row?.providerInvoicePaid) return null;
  const s = String(row.providerInvoicePaymentApprovalStatus || "not_required").trim().toLowerCase();
  if (s === "pending") return "Payment Approval Pending";
  if (s === "approved") return "Payment Approved";
  if (s === "rejected") return "Payment Approval Rejected";
  return null;
}

function canShowP2pLogisticsPayNow(row) {
  if (!row?.providerInvoiceAccepted || row?.providerInvoicePaid) return false;
  if (!row?.isP2pLinked) return true;
  if (row.providerInvoiceCanPay === true) return true;
  if (row.providerInvoiceCanPay === false) return false;
  const s = String(row.providerInvoicePaymentApprovalStatus || "not_required").trim().toLowerCase();
  return s === "not_required" || s === "approved";
}

function computeBuyerStats(allRows) {
  let pending = 0;
  let accepted = 0;
  let rejected = 0;
  let completed = 0;
  allRows.forEach((r) => {
    const k = getBuyerStatusKey(r);
    if (k === "completed") completed += 1;
    else if (k === "accepted") accepted += 1;
    else if (k === "rejected") rejected += 1;
    else pending += 1;
  });
  return { total: allRows.length, pending, accepted, rejected, completed };
}

function sentRowDetailSummary(r) {
  if (r.rfqKind === "exhibition-logistics") {
    return r.exhibitionName || "—";
  }
  if (r.rfqKind === "business-relocation") {
    return relocationScopeLabel(r.relocationScope) || "—";
  }
  if (r.industry) return r.industry;
  return r.cargoDescription || "—";
}

function filterSentRows(allRows, { search, statusFilter, kindFilter, dateFrom, dateTo }) {
  const q = search.trim().toLowerCase();
  return allRows.filter((r) => {
    if (kindFilter !== "all" && String(r.rfqKind) !== kindFilter) return false;
    if (statusFilter !== "all" && getBuyerStatusKey(r) !== statusFilter) return false;
    if (dateFrom || dateTo) {
      const t = r.submittedAt ? new Date(r.submittedAt).getTime() : NaN;
      if (!Number.isFinite(t)) return false;
      if (dateFrom && t < new Date(`${dateFrom}T00:00:00`).getTime()) return false;
      if (dateTo && t > new Date(`${dateTo}T23:59:59`).getTime()) return false;
    }
    if (!q) return true;
    const hay = [
      r.rfqReferenceNumber,
      r.contactPerson,
      r.industry,
      r.cargoDescription,
      r.originLabel,
      r.destinationLabel,
      rfqKindLabel(r.rfqKind),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return hay.includes(q);
  });
}

function SentIconAction({ label, className = "", icon: Icon, to, state, onClick, disabled, variant }) {
  const classes = [
    "lrfsent-icon-btn",
    variant ? `lrfsent-icon-btn--${variant}` : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");
  const iconEl = Icon ? <Icon aria-hidden /> : null;
  if (to && !disabled) {
    return (
      <Link
        to={to}
        state={state}
        className={classes}
        title={label}
        aria-label={label}
        data-tooltip={label}
      >
        {iconEl}
      </Link>
    );
  }
  return (
    <button
      type="button"
      className={classes}
      title={label}
      aria-label={label}
      data-tooltip={label}
      onClick={onClick}
      disabled={disabled}
    >
      {iconEl}
    </button>
  );
}

function computeSentRfqActionFlags(r, { canAcceptManifest, canActAsInvoicePayer }) {
  const dedicated = isDedicatedManagerRfq(r);
  const viewPath = buildSentRfqViewPath(r);
  const cardUi = getLogisticsRfqSentCardUi(r);
  const pickupStatus = String(r.handoverPickupStatus ?? "").trim().toLowerCase();
  const pickupConfirmed =
    pickupStatus === "picked_up" || pickupStatus === "picked up" || pickupStatus === "completed";
  const currentStageKey = r.deliveryProcess?.currentStageKey || null;
  const ewayGenerateDone = isLogisticsStageProgressDone(r.deliveryProcess?.stageProgress?.eway_generate);
  const grnGenerateDone = isLogisticsStageProgressDone(r.deliveryProcess?.stageProgress?.grn_generate);
  const ewayBillId = r.ewayBillId != null ? Number(r.ewayBillId) : null;
  const hasEWayBill = Number.isFinite(ewayBillId) && ewayBillId > 0;
  const shippingManifestId = r.shippingManifestId != null ? Number(r.shippingManifestId) : null;
  const hasManifest = Number.isFinite(shippingManifestId) && shippingManifestId > 0;
  const logisticsGrnId = r.logisticsGrnId != null ? Number(r.logisticsGrnId) : null;
  const hasGrn = Number.isFinite(logisticsGrnId) && logisticsGrnId > 0;
  const providerInvoiceId = r.providerInvoiceId != null ? Number(r.providerInvoiceId) : null;
  const hasLogisticsInvoice = Number.isFinite(providerInvoiceId) && providerInvoiceId > 0;
  const logisticsInvoiceAccepted = Boolean(r.providerInvoiceAccepted);
  const logisticsInvoicePaid = Boolean(r.providerInvoicePaid);
  const p2pPaymentApprovalLabel = getP2pInvoicePaymentApprovalLabel(r);
  const showP2pLogisticsPayNow =
    hasLogisticsInvoice &&
    logisticsInvoiceAccepted &&
    !logisticsInvoicePaid &&
    canActAsInvoicePayer &&
    canShowP2pLogisticsPayNow(r);
  const showGenerateEWay =
    pickupConfirmed &&
    currentStageKey === "eway_generate" &&
    !ewayGenerateDone &&
    r.acceptedQuoteId != null &&
    !hasEWayBill;
  const showViewEWay = pickupConfirmed && r.acceptedQuoteId != null && hasEWayBill;
  const showAcceptManifest =
    canAcceptManifest && canOfferManifestAccept(r) && hasManifest && !r.shippingManifestAccepted;
  const canCompareQuotes = Number(r.quoteCount || 0) > 0;
  const quotesPendingInternalApproval =
    r.isP2pLinked && !canCompareQuotes && (r.pendingQuoteApprovalCount ?? 0) > 0;

  return {
    dedicated,
    viewPath,
    cardUi,
    currentStageKey,
    grnGenerateDone,
    ewayBillId,
    shippingManifestId,
    hasManifest,
    logisticsGrnId,
    hasGrn,
    providerInvoiceId,
    hasLogisticsInvoice,
    logisticsInvoiceAccepted,
    logisticsInvoicePaid,
    p2pPaymentApprovalLabel,
    showP2pLogisticsPayNow,
    showGenerateEWay,
    showViewEWay,
    showAcceptManifest,
    canCompareQuotes,
    quotesPendingInternalApproval,
  };
}

/** Icon toolbar shared by card secondary actions and table Actions column. */
function SentRfqWorkflowIcons({
  row: r,
  layout = "card",
  canAcceptManifest,
  canActAsInvoicePayer,
  navigate,
  openStageModal,
  openManifestModal,
  openGrnModal,
  openBuyerInvoiceModal,
  advanceStage,
}) {
  const f = computeSentRfqActionFlags(r, { canAcceptManifest, canActAsInvoicePayer });
  const tableLayout = layout === "table";
  const btnTable = tableLayout ? "lrfsent-icon-btn--table" : "";

  const icons = (
    <>
      {f.viewPath ? (
        <>
          <SentIconAction
            label="View RFQ"
            to={f.viewPath}
            state={f.dedicated ? undefined : { from: "sent" }}
            icon={FaEye}
            variant="view"
            className={btnTable}
          />
          {!tableLayout ? <span className="lrfsent-icon-divider" aria-hidden /> : null}
        </>
      ) : null}
      {tableLayout && !f.dedicated && f.canCompareQuotes ? (
        <SentIconAction
          label={r.acceptedQuoteId ? "View quotes" : "Compare quotes"}
          icon={FaCheck}
          to={`/logistics-rfqs-sent/${r.id}/quotes`}
          variant="accept"
          className={btnTable}
        />
      ) : null}
      {tableLayout && !f.dedicated && !r.hasProvider && !f.canCompareQuotes && !f.quotesPendingInternalApproval ? (
        <SentIconAction
          label="Find logistics providers"
          icon={FaSearch}
          to="/logistics-providers"
          variant="view"
          className={btnTable}
        />
      ) : null}
      {tableLayout && !f.dedicated && f.quotesPendingInternalApproval ? (
        <span
          className={`lrfsent-icon-btn lrfsent-icon-btn--pending lrfsent-icon-btn--status ${btnTable}`.trim()}
          title="Pending Logistics Approval"
          aria-label="Pending Logistics Approval"
          data-tooltip="Pending Logistics Approval"
        >
          <FaClock aria-hidden />
        </span>
      ) : null}
      {!f.dedicated && r.acceptedQuoteId ? (
        <SentIconAction
          label={r.handoverId ? "View Handover" : "Hand over"}
          to={`/logistics-rfqs-sent/${r.id}/handover/${r.acceptedQuoteId}`}
          icon={FaBoxOpen}
          variant="handover"
          className={btnTable}
        />
      ) : null}
      {f.cardUi.showSelectStages ? (
        <SentIconAction
          label="Select stages"
          icon={FaListUl}
          variant="stages"
          className={btnTable}
          onClick={() => openStageModal(r.id)}
        />
      ) : null}
      {f.showGenerateEWay ? (
        <SentIconAction
          label="Generate E-Way Bill"
          icon={FaFileInvoice}
          variant="eway"
          className={btnTable}
          onClick={() =>
            navigate("/eway-bill-generate", {
              state: {
                logisticsQuoteId: r.acceptedQuoteId,
                logisticsSubmissionId: r.id,
                logisticsReferenceNumber: r.rfqReferenceNumber,
              },
            })
          }
        />
      ) : null}
      {f.showViewEWay ? (
        <SentIconAction
          label="View E-Way Bill"
          icon={FaFileInvoice}
          variant="eway"
          className={btnTable}
          onClick={() => navigate("/eway-generation-list", { state: { openEWayBillId: f.ewayBillId } })}
        />
      ) : null}
      {f.showAcceptManifest ? (
        <SentIconAction
          label="Accept Manifest"
          icon={FaCheckCircle}
          variant="accept"
          className={btnTable}
          onClick={() => openManifestModal(f.shippingManifestId, { allowAcceptManifest: true })}
        />
      ) : null}
      {f.hasManifest ? (
        <SentIconAction
          label="View Manifest"
          icon={FaClipboardList}
          variant="manifest"
          className={btnTable}
          onClick={() =>
            openManifestModal(f.shippingManifestId, {
              allowAcceptManifest: f.showAcceptManifest,
            })
          }
        />
      ) : null}
      {!f.dedicated &&
      f.hasManifest &&
      r.shippingManifestAccepted &&
      !f.hasGrn &&
      f.currentStageKey === "grn_generate" &&
      !f.grnGenerateDone ? (
        <SentIconAction
          label="Generate GRN"
          icon={FaFileAlt}
          variant="grn"
          className={btnTable}
          onClick={() =>
            navigate(
              `/buyer-goods-receipt-generate?manifestId=${encodeURIComponent(String(f.shippingManifestId))}&logisticsGrn=1`,
              {
                state: {
                  manifest: { id: f.shippingManifestId },
                  grnLogisticsFlow: true,
                },
              },
            )
          }
        />
      ) : null}
      {!f.dedicated && f.hasManifest && r.shippingManifestAccepted && f.hasGrn ? (
        <SentIconAction
          label="View GRN"
          icon={FaFileAlt}
          variant="grn"
          className={btnTable}
          onClick={() => openGrnModal(f.logisticsGrnId)}
        />
      ) : null}
      {f.hasLogisticsInvoice ? (
        <SentIconAction
          label="View Invoice"
          icon={FaMoneyBillWave}
          variant="invoice"
          className={btnTable}
          onClick={() => openBuyerInvoiceModal(r.id, r.rfqReferenceNumber)}
        />
      ) : null}
      {f.hasLogisticsInvoice && f.logisticsInvoiceAccepted && f.p2pPaymentApprovalLabel ? (
        <span
          className={`lrfsent-icon-btn lrfsent-icon-btn--pending lrfsent-icon-btn--status ${btnTable}`.trim()}
          title={f.p2pPaymentApprovalLabel}
          aria-label={f.p2pPaymentApprovalLabel}
          data-tooltip={f.p2pPaymentApprovalLabel}
        >
          <FaClock aria-hidden />
        </span>
      ) : null}
      {f.showP2pLogisticsPayNow ? (
        <SentIconAction
          label="Pay Now"
          icon={FaRupeeSign}
          variant="pay"
          className={btnTable}
          onClick={() =>
            navigate("/payment/gateway", {
              state: {
                paymentData: {
                  summaryTitle: `Logistics — ${r.rfqReferenceNumber}`,
                  summaryAmount: `${r.providerInvoiceCurrencyCode || ""} ${Number(r.providerInvoiceGrandTotal ?? 0).toFixed(2)}`.trim(),
                  title: "Logistics service payment",
                },
                afterSuccessNavigate: "/logistics-rfqs-sent",
                logisticsPayment: {
                  submissionId: r.id,
                  invoiceId: f.providerInvoiceId,
                },
              },
            })
          }
        />
      ) : null}
      {f.hasLogisticsInvoice && f.logisticsInvoicePaid ? (
        <span
          className={`lrfsent-icon-btn lrfsent-icon-btn--paid lrfsent-icon-btn--status ${btnTable}`.trim()}
          title="Payment completed"
          aria-label="Payment completed"
          data-tooltip="Payment completed"
        >
          <FaCheckCircle aria-hidden />
        </span>
      ) : null}
      {f.cardUi.showAdvanceStage ? (
        <SentIconAction
          label="Mark step complete"
          icon={FaCheck}
          variant="progress"
          className={btnTable}
          onClick={() => advanceStage(r.id)}
        />
      ) : null}
    </>
  );

  if (tableLayout) {
    return <div className="lrfsent-table__icon-actions">{icons}</div>;
  }
  return icons;
}

function SentMetaRow({ label, value, children }) {
  return (
    <div className="lrfsent-meta-row">
      <span className="lrfsent-meta-row__icon" aria-hidden>
        {children}
      </span>
      <div>
        <div className="lrfsent-meta-row__label">{label}</div>
        <div className="lrfsent-meta-row__value">{value ?? "—"}</div>
      </div>
    </div>
  );
}

function mapCitiesResponse(response) {
  const apiCities = Array.isArray(response?.data)
    ? response.data
    : response?.data?.cities || response?.data?.master_cities || [];
  return apiCities
    .map((city) => {
      const id = city.city_id ?? city.id ?? city.value;
      const label = city.city_name ?? city.city_label ?? city.label ?? city.name ?? city.city;
      return { id: id != null ? String(id) : "", label: label != null ? String(label) : "" };
    })
    .filter((c) => c.id && c.label);
}

async function resolveCityLabel(stateId, cityId) {
  const sid = stateId != null && String(stateId).trim() !== "" ? String(stateId).trim() : "";
  const cyid = cityId != null && String(cityId).trim() !== "" ? String(cityId).trim() : "";
  if (!cyid) return "—";
  if (!sid) return cyid;
  try {
    // Use api directly — avoid global loader on secondary enrichment calls.
    const cityRes = await api.get(`/master/company-City/${sid}`);
    const cities = mapCitiesResponse(cityRes?.data);
    return cities.find((c) => c.id === cyid)?.label || cyid;
  } catch {
    return cyid;
  }
}

function dedupeSentRowsById(rows) {
  const byId = new Map();
  for (const row of rows) {
    if (row?.id == null) continue;
    const key = sentRowKey(row);
    if (!byId.has(key)) byId.set(key, row);
  }
  return [...byId.values()];
}

async function enrichRowsWithCityLabels(rows) {
  return Promise.all(
    rows.map(async (r) => {
      if (!r.originCityId && !r.destCityId) return r;
      const [originLabel, destinationLabel] = await Promise.all([
        resolveCityLabel(r.originStateId, r.originCityId),
        resolveCityLabel(r.destStateId, r.destCityId),
      ]);
      return { ...r, originLabel, destinationLabel };
    }),
  );
}

function normalizeRows(payload) {
  const body = payload?.data ?? payload ?? {};
  const nested = body?.data ?? body;
  const raw = Array.isArray(nested?.items) ? nested.items : [];
  return raw
    .map((row) => {
      const id = row?.id != null ? Number(row.id) : NaN;
      const providerRaw = row.logisticsProviderCompanyId ?? row.logistics_provider_company_id ?? null;
      const providerCid =
        providerRaw != null && Number.isFinite(Number(providerRaw)) && Number(providerRaw) > 0
          ? Number(providerRaw)
          : null;
      const ref = row.rfqReferenceNumber ?? row.rfq_reference_number;
      const refStr = ref != null ? String(ref).trim() : "";
      const contactPerson =
        row.contactPerson ??
        row.contact_person ??
        (row.contactPersonName != null ? row.contactPersonName : null);
      const contactStr =
        contactPerson != null && String(contactPerson).trim() !== ""
          ? String(contactPerson).trim()
          : "—";
      const rfqKind = row.rfqKind ?? row.rfq_kind ?? "";
      const listSource = String(row.listSource ?? row.list_source ?? "logistics_generic").trim();
      const managerRfqStatus = row.managerRfqStatus ?? row.manager_rfq_status ?? null;
      const submittedAt = row.submittedAt ?? row.submitted_at ?? null;
      const relocationScopeRaw = row.relocationScope ?? row.relocation_scope ?? null;
      const relocationScope =
        relocationScopeRaw != null && String(relocationScopeRaw).trim() !== ""
          ? String(relocationScopeRaw).trim()
          : null;
      const industryRaw = row.industry ?? null;
      const industry =
        industryRaw != null && String(industryRaw).trim() !== "" ? String(industryRaw).trim() : null;
      const transportModeRaw = row.transportMode ?? row.transport_mode ?? null;
      const transportMode =
        transportModeRaw != null && String(transportModeRaw).trim() !== ""
          ? String(transportModeRaw).trim()
          : null;
      const exhibitionNameRaw = row.exhibitionName ?? row.exhibition_name ?? null;
      const exhibitionName =
        exhibitionNameRaw != null && String(exhibitionNameRaw).trim() !== ""
          ? String(exhibitionNameRaw).trim()
          : null;
      const exhibitionStartRaw = row.exhibitionStartDate ?? row.exhibition_start_date ?? null;
      const exhibitionStartDate =
        exhibitionStartRaw != null && String(exhibitionStartRaw).trim() !== ""
          ? String(exhibitionStartRaw).trim()
          : null;
      const exhibitionEndRaw = row.exhibitionEndDate ?? row.exhibition_end_date ?? null;
      const exhibitionEndDate =
        exhibitionEndRaw != null && String(exhibitionEndRaw).trim() !== ""
          ? String(exhibitionEndRaw).trim()
          : null;
      const quoteCountRaw = row.quoteCount ?? row.quote_count ?? 0;
      const quoteCount = Number.isFinite(Number(quoteCountRaw)) ? Number(quoteCountRaw) : 0;
      const pendingQuoteApprovalCountRaw =
        row.pendingQuoteApprovalCount ?? row.pending_quote_approval_count ?? 0;
      const pendingQuoteApprovalCount = Number.isFinite(Number(pendingQuoteApprovalCountRaw))
        ? Number(pendingQuoteApprovalCountRaw)
        : 0;
      const rejectedQuoteCountRaw = row.rejectedQuoteCount ?? row.rejected_quote_count ?? 0;
      const rejectedQuoteCount = Number.isFinite(Number(rejectedQuoteCountRaw))
        ? Number(rejectedQuoteCountRaw)
        : 0;
      const payloadRaw = row.payload ?? row.payload_json ?? null;
      const payload =
        payloadRaw != null && typeof payloadRaw === "object" && !Array.isArray(payloadRaw) ? payloadRaw : {};
      const isP2pLinked =
        row.isP2pLinked === true ||
        Boolean(payload?.p2pContext?.p2p_transaction_id || payload?.p2pContext?.p2p_organization_id);
      const cargoDescription =
        payload.cargoDescription != null && String(payload.cargoDescription).trim() !== ""
          ? String(payload.cargoDescription).trim()
          : null;
      const commodityType =
        payload.commodityType != null && String(payload.commodityType).trim() !== ""
          ? String(payload.commodityType).trim()
          : null;
      const totalWeightValue =
        payload.totalWeightValue != null && String(payload.totalWeightValue).trim() !== ""
          ? String(payload.totalWeightValue).trim()
          : null;
      const totalWeightUnit =
        payload.totalWeightUnit != null && String(payload.totalWeightUnit).trim() !== ""
          ? String(payload.totalWeightUnit).trim()
          : "KG";
      const originCityId =
        payload.originCity != null && String(payload.originCity).trim() !== ""
          ? String(payload.originCity).trim()
          : null;
      const originStateId =
        payload.originState != null && String(payload.originState).trim() !== ""
          ? String(payload.originState).trim()
          : null;
      const destCityId =
        payload.destCity != null && String(payload.destCity).trim() !== ""
          ? String(payload.destCity).trim()
          : null;
      const destStateId =
        payload.destState != null && String(payload.destState).trim() !== ""
          ? String(payload.destState).trim()
          : null;
      const acceptedQuoteIdRaw = row.acceptedQuoteId ?? row.accepted_quote_id ?? null;
      const acceptedQuoteId =
        acceptedQuoteIdRaw != null && Number.isFinite(Number(acceptedQuoteIdRaw))
          ? Number(acceptedQuoteIdRaw)
          : null;
      const handoverIdRaw = row.handoverId ?? row.handover_id ?? null;
      const handoverId =
        handoverIdRaw != null && Number.isFinite(Number(handoverIdRaw)) ? Number(handoverIdRaw) : null;
      const hpsRaw = row.handoverPickupStatus ?? row.handover_pickup_status ?? null;
      const handoverPickupStatus =
        hpsRaw != null && String(hpsRaw).trim() !== "" ? String(hpsRaw).trim() : null;
      const ewayBillIdRaw = row.ewayBillId ?? row.eway_bill_id ?? null;
      const ewayBillId =
        ewayBillIdRaw != null && Number.isFinite(Number(ewayBillIdRaw)) ? Number(ewayBillIdRaw) : null;
      const shippingManifestIdRaw = row.shippingManifestId ?? row.shipping_manifest_id ?? null;
      const shippingManifestId =
        shippingManifestIdRaw != null && Number.isFinite(Number(shippingManifestIdRaw))
          ? Number(shippingManifestIdRaw)
          : null;
      const deliveryProcessRaw = row.deliveryProcess ?? row.delivery_process ?? null;
      const deliveryProcess =
        deliveryProcessRaw != null &&
        typeof deliveryProcessRaw === "object" &&
        !Array.isArray(deliveryProcessRaw)
          ? {
              selectedStages: Array.isArray(deliveryProcessRaw.selectedStages)
                ? deliveryProcessRaw.selectedStages.map((x) => String(x))
                : [],
              stageProgress:
                deliveryProcessRaw.stageProgress && typeof deliveryProcessRaw.stageProgress === "object"
                  ? deliveryProcessRaw.stageProgress
                  : {},
              currentStageKey:
                deliveryProcessRaw.currentStageKey != null
                  ? String(deliveryProcessRaw.currentStageKey)
                  : null,
            }
          : null;
      const smAccRaw = row.shippingManifestAccepted ?? row.shipping_manifest_accepted ?? null;
      const manifestAcceptStageDone = isLogisticsStageProgressDone(deliveryProcess?.stageProgress?.manifest_accept);
      const shippingManifestAccepted =
        smAccRaw === true ||
        smAccRaw === 1 ||
        String(smAccRaw || "").trim().toLowerCase() === "true" ||
        manifestAcceptStageDone;
      const logisticsGrnIdRaw = row.logisticsGrnId ?? row.logistics_grn_id ?? null;
      const logisticsGrnId =
        logisticsGrnIdRaw != null &&
        String(logisticsGrnIdRaw).trim() !== "" &&
        Number.isFinite(Number(logisticsGrnIdRaw))
          ? Number(logisticsGrnIdRaw)
          : null;
      const providerInvoiceIdRaw =
        row.providerInvoiceId ?? row.logistics_provider_invoice_id ?? row.provider_invoice_id ?? null;
      const providerInvoiceId =
        providerInvoiceIdRaw != null &&
        String(providerInvoiceIdRaw).trim() !== "" &&
        Number.isFinite(Number(providerInvoiceIdRaw))
          ? Number(providerInvoiceIdRaw)
          : null;
      const providerInvoiceStatus =
        row.providerInvoiceStatus ?? row.logistics_provider_invoice_status ?? null;
      const providerInvoiceAcceptedRaw =
        row.providerInvoiceAccepted ?? row.logistics_provider_invoice_accepted ?? null;
      const providerInvoiceAccepted =
        providerInvoiceAcceptedRaw === true ||
        providerInvoiceAcceptedRaw === 1 ||
        String(providerInvoiceAcceptedRaw || "").trim().toLowerCase() === "true" ||
        String(providerInvoiceStatus || "").trim().toLowerCase() === "accepted";
      const providerInvoiceGrandTotalRaw =
        row.providerInvoiceGrandTotal ?? row.logistics_provider_invoice_grand_total ?? null;
      const providerInvoiceGrandTotal =
        providerInvoiceGrandTotalRaw != null && Number.isFinite(Number(providerInvoiceGrandTotalRaw))
          ? Number(providerInvoiceGrandTotalRaw)
          : null;
      const providerInvoiceCurrencyCode =
        row.providerInvoiceCurrencyCode ?? row.logistics_provider_invoice_currency_code ?? null;
      const providerInvoicePaidAtRaw =
        row.providerInvoicePaidAt ?? row.logistics_provider_invoice_paid_at ?? null;
      const providerInvoicePaidAt =
        providerInvoicePaidAtRaw != null && String(providerInvoicePaidAtRaw).trim() !== ""
          ? String(providerInvoicePaidAtRaw)
          : null;
      const providerInvoicePaidFlagRaw =
        row.providerInvoicePaid ?? row.logistics_provider_invoice_paid ?? null;
      const providerInvoicePaid =
        providerInvoicePaidAt != null ||
        providerInvoicePaidFlagRaw === true ||
        providerInvoicePaidFlagRaw === 1 ||
        String(providerInvoicePaidFlagRaw || "").trim().toLowerCase() === "true";
      const providerInvoicePaymentApprovalStatusRaw =
        row.providerInvoicePaymentApprovalStatus ??
        row.logistics_provider_invoice_payment_approval_status ??
        null;
      const providerInvoicePaymentApprovalStatus =
        providerInvoicePaymentApprovalStatusRaw != null &&
        String(providerInvoicePaymentApprovalStatusRaw).trim() !== ""
          ? String(providerInvoicePaymentApprovalStatusRaw).trim()
          : null;
      const providerInvoiceCanPayRaw = row.providerInvoiceCanPay ?? row.provider_invoice_can_pay ?? null;
      const providerInvoiceCanPay =
        providerInvoiceCanPayRaw === true ||
        providerInvoiceCanPayRaw === 1 ||
        String(providerInvoiceCanPayRaw || "").trim().toLowerCase() === "true";
      if (!Number.isFinite(id) || id <= 0 || !refStr) {
        return null;
      }
      return {
        id,
        listSource,
        managerRfqStatus:
          managerRfqStatus != null && String(managerRfqStatus).trim() !== ""
            ? String(managerRfqStatus).trim()
            : null,
        logisticsProviderCompanyId: providerCid,
        hasProvider: providerCid != null,
        rfqReferenceNumber: refStr,
        contactPerson: contactStr,
        rfqKind: String(rfqKind || "").trim(),
        submittedAt,
        relocationScope,
        industry,
        transportMode,
        exhibitionName,
        exhibitionStartDate,
        exhibitionEndDate,
        quoteCount,
        pendingQuoteApprovalCount,
        rejectedQuoteCount,
        isP2pLinked,
        cargoDescription,
        commodityType,
        totalWeightValue,
        totalWeightUnit,
        originCityId,
        originStateId,
        destCityId,
        destStateId,
        originLabel: null,
        destinationLabel: null,
        acceptedQuoteId,
        handoverId,
        handoverPickupStatus,
        ewayBillId,
        shippingManifestId,
        shippingManifestAccepted,
        logisticsGrnId,
        deliveryProcess,
        providerInvoiceId,
        providerInvoiceStatus:
          providerInvoiceStatus != null && String(providerInvoiceStatus).trim() !== ""
            ? String(providerInvoiceStatus).trim()
            : null,
        providerInvoiceAccepted,
        providerInvoiceGrandTotal,
        providerInvoiceCurrencyCode:
          providerInvoiceCurrencyCode != null && String(providerInvoiceCurrencyCode).trim() !== ""
            ? String(providerInvoiceCurrencyCode).trim()
            : null,
        providerInvoicePaid,
        providerInvoicePaidAt,
        providerInvoicePaymentApprovalStatus,
        providerInvoiceCanPay,
      };
    })
    .filter(Boolean);
}

export default function LogisticsRfqSentListPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const rfqSuccessToastShown = useRef(false);
  const loadRowsInFlightRef = useRef(false);
  const cityEnrichmentTokenRef = useRef(0);
  const rfqDeepLinkHandledRef = useRef(false);

  useEffect(() => {
    const msg = location.state?.rfqSubmittedMessage;
    if (typeof msg !== "string" || !msg.trim()) {
      rfqSuccessToastShown.current = false;
      return;
    }
    if (rfqSuccessToastShown.current) return;
    rfqSuccessToastShown.current = true;
    toast.success(msg);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    const rfqId = searchParams.get("rfqId");
    if (!rfqId || rfqDeepLinkHandledRef.current) return;
    rfqDeepLinkHandledRef.current = true;
    setSearchQuery(String(rfqId).trim());
  }, [searchParams]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [kindFilter, setKindFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [viewFormat, setViewFormat] = useState(() => {
    try {
      const stored = localStorage.getItem(LRFSENT_VIEW_STORAGE_KEY);
      if (stored === "table") return "table";
      if (stored === "card") return "card";
      return "card";
    } catch {
      return "card";
    }
  });
  const [stageModalSubId, setStageModalSubId] = useState(null);
  const [stageKeysDraft, setStageKeysDraft] = useState([]);
  const [savingStages, setSavingStages] = useState(false);
  const { shippingTermsOptions, modeOfTransportOptions } = useFactoryDetailsMaster();
  const currentRole = getCurrentRole();
  const canAcceptManifest =
    currentRole?.id !== "logistics" &&
    (currentRole?.id === 1 ||
      currentRole?.id === 2 ||
      currentRole?.id === "1" ||
      currentRole?.id === "2" ||
      currentRole?.name === "Buyer" ||
      currentRole?.name === "Seller");
  /** Buyer/Seller active role only — not logistics (payment / invoice acceptance). */
  const canActAsInvoicePayer =
    currentRole?.id !== "logistics" && (currentRole?.id === 1 || currentRole?.id === 2);

  const [manifestModalOpen, setManifestModalOpen] = useState(false);
  const [manifestLoading, setManifestLoading] = useState(false);
  const [selectedManifest, setSelectedManifest] = useState(null);
  /** When true, manifest modal shows Accept Manifest (see canOfferManifestAccept). */
  const [manifestModalAllowAccept, setManifestModalAllowAccept] = useState(false);

  const [grnModalOpen, setGrnModalOpen] = useState(false);
  const [grnModalLoading, setGrnModalLoading] = useState(false);
  const [grnModalData, setGrnModalData] = useState(null);

  const [acceptManifest, setAcceptManifest] = useState(null);
  const [isAcceptModalOpen, setIsAcceptModalOpen] = useState(false);
  const [acceptingShipment, setAcceptingShipment] = useState(false);
  const [buyerInvoiceModalOpen, setBuyerInvoiceModalOpen] = useState(false);
  const [buyerInvoiceModalLoading, setBuyerInvoiceModalLoading] = useState(false);
  const [buyerInvoiceModalData, setBuyerInvoiceModalData] = useState(null);
  const [buyerInvoiceModalRfqRef, setBuyerInvoiceModalRfqRef] = useState("");
  const [buyerInvoiceModalSubmissionId, setBuyerInvoiceModalSubmissionId] = useState(null);
  const [acceptInvoiceModalOpen, setAcceptInvoiceModalOpen] = useState(false);
  const [acceptInvoiceSubmissionId, setAcceptInvoiceSubmissionId] = useState(null);
  /** Set synchronously when opening confirm so "Yes, accept" always has a valid id (avoids stale / batched state). */
  const acceptInvoiceSubmissionIdRef = useRef(null);
  const [acceptingInvoice, setAcceptingInvoice] = useState(false);
  const [acceptForm, setAcceptForm] = useState({
    dateReceived: "",
    receivedBy: "",
    packagesReceived: "",
    condition: "Good Condition",
    notes: "",
    uploadName: "",
    uploadFile: null,
    inspectionChecklist: {},
  });

  const loadRows = useCallback(async ({ silent = false } = {}) => {
    if (loadRowsInFlightRef.current) return;
    loadRowsInFlightRef.current = true;
    if (!silent) setLoading(true);
    const enrichmentToken = cityEnrichmentTokenRef.current + 1;
    cityEnrichmentTokenRef.current = enrichmentToken;
    try {
      const res = await CommonService.get(API_URLS.LOGISTICS_PROVIDER.RFQ_MY_SUBMISSIONS, {
        detail: 1,
        _ts: Date.now(), // avoid cached 304 responses during stage-driven updates
      });
      const normalized = dedupeSentRowsById(normalizeRows(res));
      setRows(normalized);
      if (!silent) setLoading(false);

      enrichRowsWithCityLabels(normalized)
        .then((withLabels) => {
          if (cityEnrichmentTokenRef.current !== enrichmentToken) return;
          setRows(dedupeSentRowsById(withLabels));
        })
        .catch(() => {
          /* city labels are search-only; ignore enrichment failures */
        });
    } catch (e) {
      const errorMessage =
        e?.response?.data?.message || e?.response?.data?.error || e?.message || "Could not load logistics RFQs.";
      toast.error(errorMessage);
      setRows([]);
      if (!silent) setLoading(false);
    } finally {
      loadRowsInFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    let refreshTimer = null;
    const refresh = () => {
      if (document.visibilityState !== "visible") return;
      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => {
        loadRows({ silent: true });
      }, 300);
    };
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, [loadRows]);

  const dashboardHref = getDashboardRoute();

  const stats = useMemo(() => computeBuyerStats(rows), [rows]);
  const filteredRows = useMemo(
    () => filterSentRows(rows, { search: searchQuery, statusFilter, kindFilter, dateFrom, dateTo }),
    [rows, searchQuery, statusFilter, kindFilter, dateFrom, dateTo],
  );
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paginatedRows = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filteredRows.slice(start, start + PAGE_SIZE);
  }, [filteredRows, safePage]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, statusFilter, kindFilter, dateFrom, dateTo]);

  useEffect(() => {
    try {
      localStorage.setItem(LRFSENT_VIEW_STORAGE_KEY, viewFormat);
    } catch {
      /* ignore */
    }
  }, [viewFormat]);

  const resetFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setKindFilter("all");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  const openStageModal = (submissionId) => {
    setStageKeysDraft([]);
    setStageModalSubId(submissionId);
  };

  const submitStageSelection = async () => {
    if (stageModalSubId == null) return;
    const isSkipPath = stageKeysDraft.length === 0;
    setSavingStages(true);
    try {
      await CommonService.post(API_URLS.LOGISTICS_PROVIDER.RFQ_MY_SUBMISSION_DELIVERY_PROCESS(stageModalSubId), {
        stages: stageKeysDraft,
      });
      toast.success(
        isSkipPath
          ? "Delivery stages skipped — you can now generate the invoice."
          : "Your delivery steps are saved. Follow each step in order."
      );
      setStageModalSubId(null);
      await loadRows();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Could not save stages.");
    } finally {
      setSavingStages(false);
    }
  };

  const advanceStage = async (submissionId) => {
    try {
      await CommonService.post(
        API_URLS.LOGISTICS_PROVIDER.RFQ_MY_SUBMISSION_DELIVERY_PROCESS_COMPLETE_CURRENT(submissionId),
        {},
      );
      toast.success("Step marked complete.");
      await loadRows();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Could not update this step.");
    }
  };

  const closeManifestModal = () => {
    setManifestModalOpen(false);
    setSelectedManifest(null);
    setManifestModalAllowAccept(false);
  };

  const closeGrnModal = () => {
    setGrnModalOpen(false);
    setGrnModalData(null);
    setGrnModalLoading(false);
  };

  const openGrnModal = async (grnId) => {
    const id = Number(grnId);
    if (!Number.isFinite(id) || id <= 0) {
      toast.error("Invalid GRN id");
      return;
    }
    setGrnModalOpen(true);
    setGrnModalLoading(true);
    setGrnModalData(null);
    try {
      const grnResponse = await CommonService.get(`/grn/${id}`);
      if (grnResponse && grnResponse.success && grnResponse.data) {
        setGrnModalData(grnResponse.data);
      } else {
        toast.error(grnResponse?.error || "Failed to load GRN");
        setGrnModalOpen(false);
      }
    } catch (e) {
      console.error("Error fetching GRN:", e);
      toast.error(e?.response?.data?.error || e?.message || "Failed to load GRN");
      setGrnModalOpen(false);
    } finally {
      setGrnModalLoading(false);
    }
  };

  const openManifestModal = async (id, options = {}) => {
    if (id == null) return;
    const { allowAcceptManifest = false } = options;
    setManifestModalAllowAccept(Boolean(allowAcceptManifest));
    setManifestLoading(true);
    setManifestModalOpen(true);
    try {
      const res = await CommonService.get(`/shipping-manifest/${id}`);
      const raw = res?.data ?? res?.data?.data ?? res?.data?.manifest ?? res?.data?.item ?? res;
      const manifest = transformManifestData(raw);
      if (!manifest) {
        toast.error("Failed to load manifest details.");
        setManifestModalOpen(false);
        setManifestModalAllowAccept(false);
        return;
      }
      setSelectedManifest(manifest);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Failed to load manifest details.");
      setManifestModalOpen(false);
      setManifestModalAllowAccept(false);
    } finally {
      setManifestLoading(false);
    }
  };

  const formatAcceptDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleAcceptShipment = () => {
    const manifest = selectedManifest;
    if (!manifest?.id) return;
    if (manifest.status === "Accepted") {
      toast.info("This shipment has already been accepted");
      return;
    }
    setAcceptManifest(manifest);
    const today = new Date().toISOString().split("T")[0];
    setAcceptForm({
      dateReceived: formatAcceptDate(manifest.actualArrival) || today,
      receivedBy: "",
      packagesReceived: manifest.packages?.toString() || "0",
      condition: "Good Condition",
      notes: "",
      uploadName: "",
      uploadFile: null,
      inspectionChecklist: {},
    });
    setIsAcceptModalOpen(true);
  };

  const handleCloseAcceptModal = () => {
    setIsAcceptModalOpen(false);
    setAcceptManifest(null);
  };

  const closeBuyerInvoiceModal = () => {
    setBuyerInvoiceModalOpen(false);
    setBuyerInvoiceModalData(null);
    setBuyerInvoiceModalRfqRef("");
    setBuyerInvoiceModalSubmissionId(null);
    setBuyerInvoiceModalLoading(false);
  };

  const parseInvoiceFromMySubmissionResponse = (res) => {
    const pack = res?.data !== undefined ? res.data : res;
    const inner = pack?.data !== undefined ? pack.data : pack;
    return inner?.invoice ?? inner ?? null;
  };

  const openBuyerInvoiceModal = async (submissionId, rfqRefLabel) => {
    const sid = submissionId != null ? Number(submissionId) : NaN;
    if (!Number.isFinite(sid) || sid <= 0) {
      toast.error("Invalid RFQ");
      return;
    }
    setBuyerInvoiceModalOpen(true);
    setBuyerInvoiceModalLoading(true);
    setBuyerInvoiceModalData(null);
    setBuyerInvoiceModalSubmissionId(sid);
    setBuyerInvoiceModalRfqRef(rfqRefLabel != null ? String(rfqRefLabel) : "");
    try {
      const res = await CommonService.get(API_URLS.LOGISTICS_PROVIDER.RFQ_MY_SUBMISSION_INVOICE(sid));
      const inv = parseInvoiceFromMySubmissionResponse(res);
      if (!inv || inv.id == null) {
        throw new Error(res?.message || "Invoice not found");
      }
      setBuyerInvoiceModalData(inv);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e?.message || "Could not load invoice");
      setBuyerInvoiceModalOpen(false);
      setBuyerInvoiceModalData(null);
      setBuyerInvoiceModalSubmissionId(null);
    } finally {
      setBuyerInvoiceModalLoading(false);
    }
  };

  const openAcceptInvoiceConfirm = (submissionId) => {
    const sid = submissionId != null ? Number(submissionId) : NaN;
    if (!Number.isFinite(sid) || sid <= 0) return;
    acceptInvoiceSubmissionIdRef.current = sid;
    closeBuyerInvoiceModal();
    setAcceptInvoiceSubmissionId(sid);
    setAcceptInvoiceModalOpen(true);
  };

  const closeAcceptInvoiceConfirm = () => {
    if (acceptingInvoice) return;
    acceptInvoiceSubmissionIdRef.current = null;
    setAcceptInvoiceModalOpen(false);
    setAcceptInvoiceSubmissionId(null);
  };

  const confirmAcceptInvoice = async () => {
    const sidRaw = acceptInvoiceSubmissionIdRef.current ?? acceptInvoiceSubmissionId;
    const sid = sidRaw != null ? Number(sidRaw) : NaN;
    if (!Number.isFinite(sid) || sid <= 0) {
      toast.error("Could not determine which invoice to accept. Close this dialog and try again.");
      return;
    }
    setAcceptingInvoice(true);
    try {
      const res = await CommonService.post(API_URLS.LOGISTICS_PROVIDER.RFQ_MY_SUBMISSION_INVOICE_ACCEPT(sid), {});
      const acceptPayload = res?.data?.data ?? res?.data ?? {};
      const paymentApprovalStatus = String(acceptPayload.payment_approval_status || "").trim().toLowerCase();
      if (paymentApprovalStatus === "pending") {
        toast.info("Invoice accepted. Payment approval is pending.");
      } else {
        toast.success("Invoice accepted.");
      }
      acceptInvoiceSubmissionIdRef.current = null;
      setAcceptInvoiceModalOpen(false);
      setAcceptInvoiceSubmissionId(null);
      await loadRows();
      if (buyerInvoiceModalOpen && Number(buyerInvoiceModalSubmissionId) === sid) {
        try {
          const res = await CommonService.get(API_URLS.LOGISTICS_PROVIDER.RFQ_MY_SUBMISSION_INVOICE(sid));
          const inv = parseInvoiceFromMySubmissionResponse(res);
          if (inv && inv.id != null) setBuyerInvoiceModalData(inv);
        } catch {
          /* list already refreshed */
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Could not accept invoice");
    } finally {
      setAcceptingInvoice(false);
    }
  };

  const handleAcceptFormChange = (field, value) => {
    setAcceptForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAcceptFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setAcceptForm((prev) => ({
        ...prev,
        uploadFile: file,
        uploadName: file.name,
      }));
    }
  };

  const uploadPODFile = async (file) => {
    const uploadFormData = new FormData();
    uploadFormData.append("file", file);
    uploadFormData.append("category", "company");
    uploadFormData.append("companyName", acceptManifest?.exporter || "Default_Company");
    uploadFormData.append("fileType", "pod");

    const uploadResponse = await CommonService.post("/upload/single", uploadFormData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    if (uploadResponse && uploadResponse.success && uploadResponse.data) {
      return uploadResponse.data.filePath;
    }
    throw new Error("File upload failed");
  };

  const handleAcceptSubmit = async (e) => {
    e.preventDefault();
    if (!acceptManifest?.id) {
      toast.error("Manifest ID is missing");
      return;
    }
    if (!acceptForm.dateReceived || !acceptForm.receivedBy || !acceptForm.packagesReceived) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      setAcceptingShipment(true);
      let podFilePath = null;
      if (acceptForm.uploadFile) {
        try {
          podFilePath = await uploadPODFile(acceptForm.uploadFile);
        } catch (uploadError) {
          console.error("Error uploading POD file:", uploadError);
          toast.warning("POD file upload failed, but continuing with acceptance...");
        }
      }

      const acceptanceData = {
        actual_arrival_date: acceptForm.dateReceived,
        accepted_by: acceptForm.receivedBy,
        acceptance_notes: acceptForm.notes || null,
        inspection_checklist: acceptForm.inspectionChecklist || {},
        pod_file_path: podFilePath,
      };

      const response = await CommonService.put(`/shipping-manifest/${acceptManifest.id}/accept`, acceptanceData);
      if (response && response.success) {
        toast.success("Shipment accepted successfully");
        const acceptedManifestId = acceptManifest.id;
        setSelectedManifest((prev) =>
          prev && prev.id === acceptManifest.id
            ? {
                ...prev,
                status: "Accepted",
                actualArrival: acceptForm.dateReceived,
                acceptanceDetails: {
                  dateReceived: acceptForm.dateReceived,
                  acceptedDate: acceptForm.dateReceived,
                  receivedBy: acceptForm.receivedBy,
                  packagesReceived: acceptForm.packagesReceived,
                  condition: acceptForm.condition,
                  additionalNotes: acceptForm.notes,
                  podFile: acceptForm.uploadFile
                    ? { name: acceptForm.uploadName, uploadedAt: new Date().toISOString() }
                    : null,
                },
              }
            : prev,
        );
        setRows((prev) =>
          prev.map((row) =>
            Number(row.shippingManifestId) === Number(acceptedManifestId)
              ? { ...row, shippingManifestAccepted: true }
              : row,
          ),
        );
        handleCloseAcceptModal();
        await loadRows();
      } else {
        toast.error(response?.error || "Failed to accept shipment");
      }
    } catch (error) {
      console.error("Error accepting shipment:", error);
      toast.error(error?.response?.data?.error || error?.message || "Failed to accept shipment");
    } finally {
      setAcceptingShipment(false);
    }
  };

  return (
    <div className="container-fluid py-4 lrfsent-page">
      <header className="lrfsent-page-header">
        <nav className="lrfsent-breadcrumb" aria-label="Breadcrumb">
          <Link to={dashboardHref}>Dashboard</Link>
          <span className="lrfsent-breadcrumb__sep" aria-hidden>
            &gt;
          </span>
          <Link to="/logistics/rfq">Logistics</Link>
          <span className="lrfsent-breadcrumb__sep" aria-hidden>
            &gt;
          </span>
          <span className="lrfsent-breadcrumb__current">RFQ Sent</span>
        </nav>

        <div className="lrfsent-hero">
          <div className="lrfsent-hero__copy">
            <h1 className="lrfsent-hero__title">Logistics RFQs Sent</h1>
            <p className="lrfsent-hero__subtitle">
              Track all your logistics RFQs and compare quotes for the right partnership.
            </p>
          </div>
          <LrfsentHeroIllustration />
        </div>
      </header>

      <div className="lrfsent-stats">
        <div className="lrfsent-stat-card lrfsent-stat-card--total">
          <div className="lrfsent-stat-card__icon">
            <FaClipboardList aria-hidden />
          </div>
          <div>
            <div className="lrfsent-stat-card__value">{loading ? "…" : stats.total}</div>
            <div className="lrfsent-stat-card__label">Total RFQs</div>
            <div className="lrfsent-stat-card__hint">All time</div>
          </div>
        </div>
        <div className="lrfsent-stat-card lrfsent-stat-card--pending">
          <div className="lrfsent-stat-card__icon">
            <FaClock aria-hidden />
          </div>
          <div>
            <div className="lrfsent-stat-card__value">{loading ? "…" : stats.pending}</div>
            <div className="lrfsent-stat-card__label">Pending</div>
            <div className="lrfsent-stat-card__hint">Awaiting quote</div>
          </div>
        </div>
        <div className="lrfsent-stat-card lrfsent-stat-card--accepted">
          <div className="lrfsent-stat-card__icon">
            <FaCheckCircle aria-hidden />
          </div>
          <div>
            <div className="lrfsent-stat-card__value">{loading ? "…" : stats.accepted}</div>
            <div className="lrfsent-stat-card__label">Accepted</div>
            <div className="lrfsent-stat-card__hint">Quote accepted</div>
          </div>
        </div>
        <div className="lrfsent-stat-card lrfsent-stat-card--rejected">
          <div className="lrfsent-stat-card__icon">
            <FaTimesCircle aria-hidden />
          </div>
          <div>
            <div className="lrfsent-stat-card__value">{loading ? "…" : stats.rejected}</div>
            <div className="lrfsent-stat-card__label">Rejected</div>
            <div className="lrfsent-stat-card__hint">Not accepted</div>
          </div>
        </div>
      </div>

      <div className="lrfsent-filters">
        <div className="lrfsent-filters__search">
          <FaSearch className="lrfsent-filters__search-icon" aria-hidden />
          <input
            type="search"
            className="form-control"
            placeholder="Search by RFQ ID, title, company…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search RFQs"
          />
        </div>
        <select
          className="form-select lrfsent-filters__select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          aria-label="Filter by status"
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="accepted">Accepted</option>
          <option value="completed">Completed</option>
          <option value="rejected">Rejected</option>
        </select>
        <select
          className="form-select lrfsent-filters__select"
          value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value)}
          aria-label="Filter by logistics type"
        >
          <option value="all">All Types</option>
          <option value="generic">Generic Logistics</option>
          <option value="business-relocation">Business Relocation</option>
          <option value="exhibition-logistics">Exhibition Logistics</option>
        </select>
        <div className="lrfsent-filters__date-wrap">
          <FaCalendarAlt className="lrfsent-filters__date-icon" aria-hidden />
          <input
            type="date"
            className="form-control lrfsent-filters__date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            aria-label="From date"
          />
        </div>
        <div className="lrfsent-filters__date-wrap">
          <FaCalendarAlt className="lrfsent-filters__date-icon" aria-hidden />
          <input
            type="date"
            className="form-control lrfsent-filters__date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            aria-label="To date"
          />
        </div>
        <button type="button" className="lrfsent-filters__reset" onClick={resetFilters}>
          <FaRedo aria-hidden />
          Reset
        </button>
      </div>

      {loading ? (
        <div className="lrfsent-loading">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : rows.length === 0 ? (
        <div className="lrfsent-empty">
          <FaInbox className="lrfsent-empty__icon" aria-hidden />
          <h5 className="text-secondary fw-semibold">No logistics RFQs found</h5>
          <p className="text-muted mb-3">Submit an RFQ from the home page or logistics directory to see it here.</p>
          <Link to="/logistics/rfq" className="btn btn-primary me-2">
            <FaPaperPlane className="me-2" aria-hidden />
            Send RFQ
          </Link>
          <Link to="/logistics-providers" className="btn btn-outline-primary">
            <FaSearch className="me-2" aria-hidden />
            Find logistics providers
          </Link>
        </div>
      ) : filteredRows.length === 0 ? (
        <div className="lrfsent-empty">
          <FaInbox className="lrfsent-empty__icon" aria-hidden />
          <h5 className="text-secondary fw-semibold">No RFQs match your filters</h5>
          <button type="button" className="btn btn-outline-primary btn-sm mt-2" onClick={resetFilters}>
            Clear filters
          </button>
        </div>
      ) : (
        <>
        <div className="lrfsent-view-toolbar">
          <span className="lrfsent-view-toolbar__meta">
            {filteredRows.length} RFQ{filteredRows.length === 1 ? "" : "s"} • Page {safePage} of {totalPages}{" "}
            (up to {PAGE_SIZE} per page)
          </span>
          <div className="lrfsent-view-toggle" role="group" aria-label="Display format">
            <button
              type="button"
              className={`lrfsent-view-toggle__btn ${viewFormat === "card" ? "lrfsent-view-toggle__btn--active" : ""}`}
              onClick={() => setViewFormat("card")}
              aria-pressed={viewFormat === "card"}
              aria-label="Card view"
              title="Card view"
            >
              <FaThLarge aria-hidden />
            </button>
            <button
              type="button"
              className={`lrfsent-view-toggle__btn ${viewFormat === "table" ? "lrfsent-view-toggle__btn--active" : ""}`}
              onClick={() => setViewFormat("table")}
              aria-pressed={viewFormat === "table"}
              aria-label="Table view"
              title="Table view"
            >
              <FaTable aria-hidden />
            </button>
          </div>
        </div>

        {viewFormat === "table" ? (
          <div className="lrfsent-table-wrap">
            <table className="lrfsent-table">
              <thead>
                <tr>
                  <th scope="col">RFQ reference</th>
                  <th scope="col">Type</th>
                  <th scope="col">Contact</th>
                  <th scope="col">Details</th>
                  <th scope="col">Submitted</th>
                  <th scope="col">Status</th>
                  <th scope="col" className="lrfsent-table__col-actions">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedRows.map((r) => {
                  const statusPill = getBuyerStatusPill(r);
                  const kindVisual = getKindTableVisual(r.rfqKind);
                  const KindIcon = kindVisual.Icon;

                  return (
                    <tr key={`rfq-table-${sentRowKey(r)}`}>
                      <td>
                        <div className="lrfsent-table__ref-cell" title={r.rfqReferenceNumber}>
                          <span
                            className={`lrfsent-table__kind-icon lrfsent-table__kind-icon--${kindVisual.tone}`}
                          >
                            <KindIcon aria-hidden />
                          </span>
                          <span className="lrfsent-table__ref-text">{r.rfqReferenceNumber}</span>
                        </div>
                      </td>
                      <td>
                        <div className="lrfsent-table__type-cell">
                          <span
                            className={`lrfsent-table__kind-icon lrfsent-table__kind-icon--sm lrfsent-table__kind-icon--${kindVisual.tone}`}
                          >
                            <KindIcon aria-hidden />
                          </span>
                          <span className={`lrfsent-table__type-label lrfsent-table__type-label--${kindVisual.tone}`}>
                            {rfqKindLabel(r.rfqKind)}
                          </span>
                        </div>
                      </td>
                      <td>{r.contactPerson || "—"}</td>
                      <td className="lrfsent-table__details">{sentRowDetailSummary(r)}</td>
                      <td>{formatDate(r.submittedAt)}</td>
                      <td>
                        <span className={statusPill.className} title={statusPill.title}>
                          {statusPill.label}
                        </span>
                      </td>
                      <td className="lrfsent-table__actions">
                        <SentRfqWorkflowIcons
                          row={r}
                          layout="table"
                          canAcceptManifest={canAcceptManifest}
                          canActAsInvoicePayer={canActAsInvoicePayer}
                          navigate={navigate}
                          openStageModal={openStageModal}
                          openManifestModal={openManifestModal}
                          openGrnModal={openGrnModal}
                          openBuyerInvoiceModal={openBuyerInvoiceModal}
                          advanceStage={advanceStage}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
        <div className="lrfsent-list">
          {paginatedRows.map((r) => {
            const dedicated = isDedicatedManagerRfq(r);
            const viewPath = buildSentRfqViewPath(r);
            const cardUi = getLogisticsRfqSentCardUi(r);
            const pickupStatus = String(r.handoverPickupStatus ?? "").trim().toLowerCase();
            const pickupConfirmed =
              pickupStatus === "picked_up" || pickupStatus === "picked up" || pickupStatus === "completed";
            const currentStageKey = r.deliveryProcess?.currentStageKey || null;
            const ewayGenerateDone = isLogisticsStageProgressDone(r.deliveryProcess?.stageProgress?.eway_generate);
            const manifestAcceptDone = isLogisticsStageProgressDone(r.deliveryProcess?.stageProgress?.manifest_accept);
            const grnGenerateDone = isLogisticsStageProgressDone(r.deliveryProcess?.stageProgress?.grn_generate);
            const ewayBillId = r.ewayBillId != null ? Number(r.ewayBillId) : null;
            const hasEWayBill = Number.isFinite(ewayBillId) && ewayBillId > 0;
            const shippingManifestId = r.shippingManifestId != null ? Number(r.shippingManifestId) : null;
            const hasManifest = Number.isFinite(shippingManifestId) && shippingManifestId > 0;
            const logisticsGrnId = r.logisticsGrnId != null ? Number(r.logisticsGrnId) : null;
            const hasGrn = Number.isFinite(logisticsGrnId) && logisticsGrnId > 0;
            const providerInvoiceId = r.providerInvoiceId != null ? Number(r.providerInvoiceId) : null;
            const hasLogisticsInvoice = Number.isFinite(providerInvoiceId) && providerInvoiceId > 0;
            const logisticsInvoiceAccepted = Boolean(r.providerInvoiceAccepted);
            const logisticsInvoicePaid = Boolean(r.providerInvoicePaid);
            const p2pPaymentApprovalLabel = getP2pInvoicePaymentApprovalLabel(r);
            const showP2pLogisticsPayNow =
              hasLogisticsInvoice &&
              logisticsInvoiceAccepted &&
              !logisticsInvoicePaid &&
              canActAsInvoicePayer &&
              canShowP2pLogisticsPayNow(r);
            const showGenerateEWay =
              pickupConfirmed &&
              currentStageKey === "eway_generate" &&
              !ewayGenerateDone &&
              r.acceptedQuoteId != null &&
              !hasEWayBill;
            const showViewEWay = pickupConfirmed && r.acceptedQuoteId != null && hasEWayBill;
            const showAcceptManifest =
              canAcceptManifest && canOfferManifestAccept(r) && hasManifest && !r.shippingManifestAccepted;
            const statusPill = getBuyerStatusPill(r);
            const canCompareQuotes = r.quoteCount > 0;
            const quotesPendingInternalApproval =
              r.isP2pLinked &&
              !canCompareQuotes &&
              (r.pendingQuoteApprovalCount ?? 0) > 0;

            return (
              <article key={`rfq-sent-${sentRowKey(r)}`} className="lrfsent-card">
                <div className="lrfsent-card__head">
                  <div className="lrfsent-card__head-left">
                    <span className="lrfsent-card__ref" title={r.rfqReferenceNumber}>
                      {r.rfqReferenceNumber}
                    </span>
                    <span className={statusPill.className}>{statusPill.label}</span>
                  </div>
                  <div className="dropdown">
                    <button
                      type="button"
                      className="lrfsent-card__menu"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                      aria-label="RFQ options"
                    >
                      <FaEllipsisV aria-hidden />
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end shadow-sm">
                      {viewPath ? (
                        <li>
                          <Link className="dropdown-item" to={viewPath} state={{ from: "sent" }}>
                            View RFQ details
                          </Link>
                        </li>
                      ) : null}
                      {canCompareQuotes ? (
                        <li>
                          <Link className="dropdown-item" to={`/logistics-rfqs-sent/${r.id}/quotes`}>
                            {r.acceptedQuoteId ? "View quotes" : "Compare quotes"}
                          </Link>
                        </li>
                      ) : null}
                      {r.acceptedQuoteId ? (
                        <li>
                          <Link
                            className="dropdown-item"
                            to={`/logistics-rfqs-sent/${r.id}/handover/${r.acceptedQuoteId}`}
                          >
                            {r.handoverId ? "View handover" : "Hand over"}
                          </Link>
                        </li>
                      ) : null}
                      {showGenerateEWay ? (
                        <li>
                          <button
                            type="button"
                            className="dropdown-item"
                            onClick={() =>
                              navigate("/eway-bill-generate", {
                                state: {
                                  logisticsQuoteId: r.acceptedQuoteId,
                                  logisticsSubmissionId: r.id,
                                  logisticsReferenceNumber: r.rfqReferenceNumber,
                                },
                              })
                            }
                          >
                            Generate E-Way Bill
                          </button>
                        </li>
                      ) : null}
                      {showViewEWay ? (
                        <li>
                          <button
                            type="button"
                            className="dropdown-item"
                            onClick={() =>
                              navigate("/eway-generation-list", { state: { openEWayBillId: ewayBillId } })
                            }
                          >
                            View E-Way Bill
                          </button>
                        </li>
                      ) : null}
                      {showAcceptManifest ? (
                        <li>
                          <button
                            type="button"
                            className="dropdown-item"
                            onClick={() =>
                              openManifestModal(shippingManifestId, { allowAcceptManifest: true })
                            }
                          >
                            Accept Manifest
                          </button>
                        </li>
                      ) : null}
                      {hasManifest ? (
                        <li>
                          <button
                            type="button"
                            className="dropdown-item"
                            onClick={() =>
                              openManifestModal(shippingManifestId, {
                                allowAcceptManifest: showAcceptManifest,
                              })
                            }
                          >
                            View Manifest
                          </button>
                        </li>
                      ) : null}
                      {!dedicated &&
                    hasManifest && r.shippingManifestAccepted && hasGrn ? (
                        <li>
                          <button type="button" className="dropdown-item" onClick={() => openGrnModal(logisticsGrnId)}>
                            View GRN
                          </button>
                        </li>
                      ) : null}
                      {hasLogisticsInvoice ? (
                        <li>
                          <button
                            type="button"
                            className="dropdown-item"
                            onClick={() => openBuyerInvoiceModal(r.id, r.rfqReferenceNumber)}
                          >
                            View invoice
                          </button>
                        </li>
                      ) : null}
                      {cardUi.showSelectStages ? (
                        <li>
                          <button type="button" className="dropdown-item" onClick={() => openStageModal(r.id)}>
                            Select delivery stages
                          </button>
                        </li>
                      ) : null}
                      {cardUi.showAdvanceStage ? (
                        <li>
                          <button type="button" className="dropdown-item" onClick={() => advanceStage(r.id)}>
                            Mark step complete
                          </button>
                        </li>
                      ) : null}
                    </ul>
                  </div>
                </div>

                <h3 className={getKindTitleClass(r.rfqKind)}>{rfqKindLabel(r.rfqKind)}</h3>

                <div className="lrfsent-card__details">
                    <div className="lrfsent-meta">
                      <SentMetaRow label="Contact" value={r.contactPerson}>
                        <FaUser />
                      </SentMetaRow>
                      {r.rfqKind === "generic" ? (
                        <>
                          <SentMetaRow label="Industry" value={r.industry}>
                            <FaIndustry />
                          </SentMetaRow>
                          <SentMetaRow label="Mode of Transport" value={transportModeLabel(r.transportMode)}>
                            <FaTruck />
                          </SentMetaRow>
                        </>
                      ) : null}
                      {r.rfqKind === "business-relocation" ? (
                        <SentMetaRow label="Relocation" value={relocationScopeLabel(r.relocationScope)}>
                          <FaBuilding />
                        </SentMetaRow>
                      ) : null}
                      {r.rfqKind === "exhibition-logistics" ? (
                        <SentMetaRow label="Exhibition" value={r.exhibitionName}>
                          <FaStar />
                        </SentMetaRow>
                      ) : null}
                      {r.rfqKind === "exhibition-logistics" && r.transportMode ? (
                        <SentMetaRow label="Mode of Transport" value={transportModeLabel(r.transportMode)}>
                          <FaTruck />
                        </SentMetaRow>
                      ) : null}
                      {r.rfqKind === "business-relocation" && r.transportMode ? (
                        <SentMetaRow label="Mode of Transport" value={transportModeLabel(r.transportMode)}>
                          <FaTruck />
                        </SentMetaRow>
                      ) : null}
                      <SentMetaRow label="Submitted On" value={formatDate(r.submittedAt)}>
                        <FaCalendarAlt />
                      </SentMetaRow>
                      {!r.hasProvider && !dedicated ? (
                        <SentMetaRow label="Provider" value="Not assigned yet">
                          <FaTruck />
                        </SentMetaRow>
                      ) : null}
                      {dedicated ? (
                        <SentMetaRow label="Tracking" value="With logistics team">
                          <FaPaperPlane />
                        </SentMetaRow>
                      ) : null}
                    </div>
                </div>

                <div className="lrfsent-card__actions">
                  {dedicated ? (
                    viewPath ? (
                      <Link to={viewPath} className="lrfsent-btn lrfsent-btn--block lrfsent-btn--accept-outline">
                        View request
                      </Link>
                    ) : null
                  ) : canCompareQuotes ? (
                    <Link
                      to={`/logistics-rfqs-sent/${r.id}/quotes`}
                      className={`lrfsent-btn lrfsent-btn--block ${r.acceptedQuoteId ? "lrfsent-btn--success" : "lrfsent-btn--accept"}`}
                    >
                      <FaCheck aria-hidden />
                      {r.acceptedQuoteId ? "View Quotes" : "Compare Quotes"}
                    </Link>
                  ) : !r.hasProvider ? (
                    <Link to="/logistics-providers" className="lrfsent-btn lrfsent-btn--block lrfsent-btn--accept-outline">
                      Find providers
                    </Link>
                  ) : quotesPendingInternalApproval ? (
                    <span
                      className="lrfsent-btn lrfsent-btn--block lrfsent-btn--accept-outline"
                      style={{ opacity: 0.7, cursor: "default", pointerEvents: "none" }}
                      aria-disabled="true"
                    >
                      Pending Logistics Approval
                    </span>
                  ) : null}
                  <div className="lrfsent-card__actions-secondary">
                    <SentRfqWorkflowIcons
                      row={r}
                      layout="card"
                      canAcceptManifest={canAcceptManifest}
                      canActAsInvoicePayer={canActAsInvoicePayer}
                      navigate={navigate}
                      openStageModal={openStageModal}
                      openManifestModal={openManifestModal}
                      openGrnModal={openGrnModal}
                      openBuyerInvoiceModal={openBuyerInvoiceModal}
                      advanceStage={advanceStage}
                    />
                  </div>
                </div>
              </article>
            );
          })}
        </div>
        )}

        <div className="lrfsent-pagination">
          <span>
            Showing {(safePage - 1) * PAGE_SIZE + 1} to {Math.min(safePage * PAGE_SIZE, filteredRows.length)} of{" "}
            {filteredRows.length} RFQ{filteredRows.length === 1 ? "" : "s"}
          </span>
          <div className="lrfsent-pagination__controls">
            <button
              type="button"
              className="lrfsent-pagination__btn"
              disabled={safePage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .slice(Math.max(0, safePage - 3), safePage + 2)
              .map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`lrfsent-pagination__btn ${n === safePage ? "lrfsent-pagination__btn--active" : ""}`}
                  onClick={() => setPage(n)}
                >
                  {n}
                </button>
              ))}
            <button
              type="button"
              className="lrfsent-pagination__btn"
              disabled={safePage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </div>
        </div>
        </>
      )}

      {stageModalSubId != null ? (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          style={{ backgroundColor: "rgba(15, 23, 42, 0.45)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Select delivery stages</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => setStageModalSubId(null)}
                />
              </div>
              <div className="modal-body">
                <p className="text-muted small mb-3">
                  Tick the steps that apply. They will run in a fixed order after you save (E-Way → manifest →
                  transit → GRN, as applicable).
                </p>
                <div className="list-group">
                  {DELIVERY_STAGE_CHECKBOXES.map((opt) => (
                    <label key={opt.key} className="list-group-item d-flex gap-2 align-items-center cursor-pointer">
                      <input
                        className="form-check-input m-0"
                        type="checkbox"
                        checked={stageKeysDraft.includes(opt.key)}
                        onChange={(e) => {
                          setStageKeysDraft((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(opt.key);
                            else next.delete(opt.key);
                            return [...next];
                          });
                        }}
                      />
                      <span className="small">{opt.label}</span>
                    </label>
                  ))}
                </div>
                {stageKeysDraft.length === 0 ? (
                  <p
                    className="small mt-3 mb-0"
                    style={{
                      background: "#fff7ed",
                      border: "1px dashed #fdba74",
                      color: "#9a3412",
                      padding: "10px 12px",
                      borderRadius: 6,
                    }}
                  >
                    No stages selected — submitting will skip post-pickup tracking and let you move
                    straight to invoice creation.
                  </p>
                ) : null}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setStageModalSubId(null)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className={stageKeysDraft.length === 0 ? "btn btn-warning" : "btn btn-primary"}
                  disabled={savingStages}
                  onClick={submitStageSelection}
                >
                  {savingStages
                    ? "Saving…"
                    : stageKeysDraft.length === 0
                      ? "Skip & continue to invoice"
                      : "Save & start"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <ShippingManifestDetailsModal
        open={manifestModalOpen}
        manifest={selectedManifest}
        onClose={closeManifestModal}
        shippingTermsOptions={shippingTermsOptions}
        modeOfTransportOptions={modeOfTransportOptions}
        showAcceptButton={Boolean(
          manifestModalAllowAccept &&
            canAcceptManifest &&
            selectedManifest?.status !== "Accepted" &&
            !manifestLoading,
        )}
        onAccept={handleAcceptShipment}
        acceptDisabled={acceptingShipment || manifestLoading}
        acceptLabel="Accept Manifest"
      />

      <LogisticsGrnDetailsModal
        open={grnModalOpen}
        onClose={closeGrnModal}
        grn={grnModalData}
        loading={grnModalLoading}
      />

      {acceptInvoiceModalOpen ? (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-labelledby="accept-invoice-modal-title"
          style={{ backgroundColor: "rgba(15, 23, 42, 0.45)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="accept-invoice-modal-title">
                  Accept invoice
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={closeAcceptInvoiceConfirm}
                  disabled={acceptingInvoice}
                />
              </div>
              <div className="modal-body">
                <p className="mb-0">Are you sure you want to accept this invoice?</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={closeAcceptInvoiceConfirm}
                  disabled={acceptingInvoice}
                >
                  Cancel
                </button>
                <button type="button" className="btn btn-success" onClick={confirmAcceptInvoice} disabled={acceptingInvoice}>
                  {acceptingInvoice ? "Accepting…" : "Yes, accept"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {buyerInvoiceModalOpen ? (
        <div className="grn-manifest-list-modal-wrapper">
          <div className="grn-generate">
            <div className="grn-preview-modal-overlay" onClick={closeBuyerInvoiceModal} role="presentation">
              <div
                className="grn-preview-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="buyer-logistics-invoice-modal-title"
                onClick={(e) => e.stopPropagation()}
                tabIndex={-1}
              >
                <button
                  type="button"
                  className="grn-preview-modal-close"
                  onClick={closeBuyerInvoiceModal}
                  aria-label="Close invoice details"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 6 6 18" />
                    <path d="m6 6 12 12" />
                  </svg>
                </button>
                <div className="grn-preview-modal-header">
                  <div>
                    <h2 id="buyer-logistics-invoice-modal-title" className="grn-preview-modal-title">
                      Logistics invoice
                      {buyerInvoiceModalRfqRef ? (
                        <span className="text-muted fw-normal ms-1 small font-monospace">{buyerInvoiceModalRfqRef}</span>
                      ) : null}
                    </h2>
                    <p className="grn-preview-modal-subtitle">Saved invoice for this RFQ submission.</p>
                  </div>
                </div>
                <div className="grn-preview-content">
                  {buyerInvoiceModalLoading ? (
                    <div className="p-4 text-center text-muted">Loading invoice…</div>
                  ) : !buyerInvoiceModalData ? (
                    <div className="p-4 text-center text-muted">No invoice data.</div>
                  ) : (
                    <div className="px-2 pb-3">
                      <div className="row g-2 small mb-3">
                        <div className="col-md-4">
                          <strong>Invoice #</strong>
                          <div className="font-monospace">{buyerInvoiceModalData.invoiceNumber ?? "—"}</div>
                        </div>
                        <div className="col-md-4">
                          <strong>Date</strong>
                          <div>{buyerInvoiceModalData.invoiceDate ?? "—"}</div>
                        </div>
                        <div className="col-md-4">
                          <strong>Due</strong>
                          <div>{buyerInvoiceModalData.dueDate ?? "—"}</div>
                        </div>
                        <div className="col-md-4">
                          <strong>RFQ ref</strong>
                          <div>{buyerInvoiceModalData.rfqReference ?? "—"}</div>
                        </div>
                        <div className="col-md-4">
                          <strong>Currency</strong>
                          <div>{buyerInvoiceModalData.currencyCode ?? "—"}</div>
                        </div>
                        <div className="col-md-4">
                          <strong>Status</strong>
                          <div>{buyerInvoiceModalData.status ?? "—"}</div>
                        </div>
                      </div>
                      <div className="row g-3 small mb-3">
                        <div className="col-md-6">
                          <h6 className="text-secondary">Provider</h6>
                          <div>{buyerInvoiceModalData.provider?.companyName ?? "—"}</div>
                          <div className="text-muted" style={{ whiteSpace: "pre-wrap" }}>
                            {buyerInvoiceModalData.provider?.address || ""}
                          </div>
                          <div className="mt-1">GSTIN: {buyerInvoiceModalData.provider?.gstin ?? "—"}</div>
                        </div>
                        <div className="col-md-6">
                          <h6 className="text-secondary">Buyer</h6>
                          <div>{buyerInvoiceModalData.buyer?.companyName ?? "—"}</div>
                          <div className="text-muted" style={{ whiteSpace: "pre-wrap" }}>
                            {buyerInvoiceModalData.buyer?.address || ""}
                          </div>
                          <div className="mt-1">Tax ID: {buyerInvoiceModalData.buyer?.taxId ?? "—"}</div>
                        </div>
                      </div>
                      <table className="table table-sm table-bordered mb-2">
                        <tbody>
                          <tr>
                            <th>Subtotal</th>
                            <td className="text-end">{Number(buyerInvoiceModalData.subtotal ?? 0).toFixed(2)}</td>
                          </tr>
                          <tr>
                            <th>Tax ({Number(buyerInvoiceModalData.taxPercent ?? 0).toFixed(2)}%)</th>
                            <td className="text-end">{Number(buyerInvoiceModalData.taxAmount ?? 0).toFixed(2)}</td>
                          </tr>
                          <tr className="table-light fw-bold">
                            <th>Grand total</th>
                            <td className="text-end">{Number(buyerInvoiceModalData.grandTotal ?? 0).toFixed(2)}</td>
                          </tr>
                        </tbody>
                      </table>
                      {buyerInvoiceModalData.notes ? (
                        <div className="small text-muted mb-0">
                          <strong>Notes</strong>
                          <div style={{ whiteSpace: "pre-wrap" }}>{buyerInvoiceModalData.notes}</div>
                        </div>
                      ) : null}
                      {buyerInvoiceModalData.acceptedAt || buyerInvoiceModalData.acceptedByUserLoginId != null ? (
                        <div className="small text-muted border-top pt-2 mt-2 mb-0">
                          {buyerInvoiceModalData.acceptedAt ? (
                            <div>
                              <strong>Accepted</strong> {formatInvoiceAcceptedDisplay(buyerInvoiceModalData.acceptedAt)}
                            </div>
                          ) : null}
                          {buyerInvoiceModalData.acceptedByUserLoginId != null ? (
                            <div className="mt-1">
                              <strong>Accepted by</strong>{" "}
                              <span className="font-monospace">user #{buyerInvoiceModalData.acceptedByUserLoginId}</span>
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
                <div className="grn-preview-modal-footer d-flex justify-content-end gap-2 flex-wrap">
                  {!buyerInvoiceModalLoading &&
                  buyerInvoiceModalData &&
                  canActAsInvoicePayer &&
                  !buyerInvoiceModalData.acceptedAt &&
                  String(buyerInvoiceModalData.status || "").trim().toLowerCase() !== "accepted" ? (
                    <button
                      type="button"
                      className="btn btn-success btn-sm"
                      disabled={buyerInvoiceModalSubmissionId == null}
                      onClick={() => openAcceptInvoiceConfirm(buyerInvoiceModalSubmissionId)}
                    >
                      <i className="fas fa-check-circle me-1" aria-hidden />
                      Accept invoice
                    </button>
                  ) : null}
                  <button type="button" className="btn btn-secondary btn-sm" onClick={closeBuyerInvoiceModal}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {isAcceptModalOpen && acceptManifest ? (
        <div className="shipping-manifest-list">
          <div className="sml-accept-modal-overlay" onClick={handleCloseAcceptModal}>
            <div
              className="sml-accept-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="sml-accept-title"
              aria-describedby="sml-accept-description"
              tabIndex={-1}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="sml-view-modal-close"
                onClick={handleCloseAcceptModal}
                aria-label="Close accept shipment"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>

              <div className="sml-accept-header">
                <div>
                  <h2 id="sml-accept-title">Accept Shipment - {acceptManifest.manifestNumber}</h2>
                  <p id="sml-accept-description">Review shipment details and confirm acceptance.</p>
                </div>
              </div>

            <div className="sml-accept-stats">
              <div>
                <p className="sml-accept-stat-label">Expected Packages</p>
                <p className="sml-accept-stat-value">{acceptManifest.packages}</p>
              </div>
              <div>
                <p className="sml-accept-stat-label">Gross Weight</p>
                <p className="sml-accept-stat-value">{acceptManifest.weight}</p>
              </div>
              <div>
                <p className="sml-accept-stat-label">Invoices</p>
                <p className="sml-accept-stat-value">{acceptManifest.invoiceCount}</p>
              </div>
            </div>

            <form className="sml-accept-form" onSubmit={handleAcceptSubmit}>
              <div className="sml-accept-card">
                <h4>Receiving Details</h4>
                <div className="sml-accept-grid sml-accept-grid-two">
                  <label>
                    <span>Date Received *</span>
                    <input
                      type="date"
                      className="input-control"
                      value={acceptForm.dateReceived}
                      onChange={(e) => handleAcceptFormChange("dateReceived", e.target.value)}
                      required
                      disabled={acceptingShipment}
                    />
                  </label>
                  <label>
                    <span>Received By *</span>
                    <input
                      type="text"
                      className="input-control"
                      placeholder="Name of person receiving"
                      value={acceptForm.receivedBy}
                      onChange={(e) => handleAcceptFormChange("receivedBy", e.target.value)}
                      required
                      disabled={acceptingShipment}
                    />
                  </label>
                  <label>
                    <span>Number of Packages Received *</span>
                    <input
                      type="number"
                      className="input-control"
                      min="0"
                      value={acceptForm.packagesReceived}
                      onChange={(e) => handleAcceptFormChange("packagesReceived", e.target.value)}
                      required
                      disabled={acceptingShipment}
                    />
                  </label>
                  <label>
                    <span>Condition *</span>
                    <select
                      className="input-control input-control-select"
                      value={acceptForm.condition}
                      onChange={(e) => handleAcceptFormChange("condition", e.target.value)}
                      disabled={acceptingShipment}
                    >
                      <option>Good Condition</option>
                      <option>Minor Damage</option>
                      <option>Major Damage</option>
                    </select>
                  </label>
                </div>
              </div>

              <div className="sml-accept-row">
                <div className="sml-accept-card">
                  <h4>Inspection Checklist</h4>
                  <div className="sml-accept-checklist">
                    {[
                      { key: "packagingIntact", label: "Packaging is intact and undamaged" },
                      { key: "correctQuantity", label: "Correct quantity received as per manifest" },
                      { key: "noVisibleDamage", label: "No visible damage to goods" },
                      { key: "sealsIntact", label: "Container seals are intact and match manifest" },
                      { key: "documentsComplete", label: "All shipping documents are complete and correct" },
                    ].map((item) => (
                      <label key={item.key}>
                        <input
                          type="checkbox"
                          checked={acceptForm.inspectionChecklist[item.key] || false}
                          onChange={(e) => {
                            setAcceptForm((prev) => ({
                              ...prev,
                              inspectionChecklist: {
                                ...prev.inspectionChecklist,
                                [item.key]: e.target.checked,
                              },
                            }));
                          }}
                          disabled={acceptingShipment}
                        />
                        {item.label}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="sml-accept-card">
                  <h4>Additional Notes</h4>
                  <textarea
                    className="sml-accept-notes"
                    placeholder="Any remarks about receiving condition"
                    value={acceptForm.notes}
                    onChange={(e) => handleAcceptFormChange("notes", e.target.value)}
                    rows="6"
                    disabled={acceptingShipment}
                  />
                </div>
              </div>

              <div className="sml-accept-card">
                <h4>Upload POD (Optional)</h4>
                <div className="sml-upload-row">
                  <input
                    type="file"
                    className="input-control"
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={handleAcceptFileChange}
                    disabled={acceptingShipment}
                  />
                  {acceptForm.uploadName ? <span className="sml-upload-name">{acceptForm.uploadName}</span> : null}
                </div>
              </div>

              <div className="sml-accept-actions">
                <button
                  type="button"
                  className="sml-btn sml-btn-outline"
                  onClick={handleCloseAcceptModal}
                  disabled={acceptingShipment}
                >
                  Cancel
                </button>
                <button type="submit" className="sml-btn sml-btn-primary" disabled={acceptingShipment}>
                  {acceptingShipment ? "Accepting..." : "Confirm Acceptance"}
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
